import {
  ChevronLeftIcon,
  InfoCircleIcon,
  PlusIcon,
} from '@langchain/untitled-ui-icons';
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Switch,
} from '@mui/joy';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { mutate } from 'swr';

import { getFeedbackConfigSubtitle } from '@/Pages/AnnotationQueue/utils/getFeedbackConfigSubtitle';
import { DatasetPicker } from '@/components/DatasetPicker';
import { Pane } from '@/components/Pane';
import { FetcherParams } from '@/data/fetcher';
import { Tooltip } from '@/design-system';
import {
  useAnnotationQueue,
  useCreateAnnotationQueueMutation,
  useUpdateAnnotationQueueMutation,
} from '@/hooks/useSwr';
import { useCreateTagsOnResource } from '@/hooks/useTagsOnResource';
import EditIcon from '@/icons/EditIcon.svg?react';
import SimpleEditIcon from '@/icons/SimpleEditIcon.svg?react';
import {
  AnnotationQueueRubricItem,
  AnnotationQueueSchema,
  FeedbackConfigSchema,
  ResourceType,
} from '@/types/schema';
import { apiAnnotationQueuesPath } from '@/utils/constants';
import { lowerCaseIncludes } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { FeedbackChip } from '../FeedbackChips';
import { FeedbackConfigForm } from '../FeedbackConfig/FeedbackConfigForm';
import { TopBarPaneSlot } from '../Pane.utils';
import { PopoverDropdownMenu } from '../PopoverDropdownMenu';
import { ResourceTagCrudPaneEditor } from '../ResourceTagFilter/ResourceTagCrudPaneEditor';
import useToast from '../Toast/useToast';
import { AnnotationQueueRubricCrud } from './AnnotationQueueRubricCrud';

type AnnotationQueueCrudProps = {
  closeModal: () => void;
  onSuccess: (aq: AnnotationQueueSchema) => void;
  existingAnnotationQueue?: AnnotationQueueSchema;
  focusOn?: 'instructions' | 'feedback';
  sessionIds?: string[];
  numRunsToAnnotate?: number;
  sessionNamesToAnnotate?: string[];
};

type AnnotationQueueCrudPaneProps = AnnotationQueueCrudProps & {
  open: boolean;
};

export interface AnnotationQueueFormInput {
  name: string;
  description: string;
  datasetId: string | null;
  numReviewersPerItem: string | null;
  allWorkspaceMembersReview: boolean | null;
  enableReservations: boolean;
  reservationHours: number;
  reservationMinutes: number;
  feedbackRubricItems?: AnnotationQueueRubricItem[];
  rubricInstructions?: string;
}

export const AddFeedbackPopoverWithButton = (props: {
  onSelect: (value: FeedbackConfigSchema) => void;
  onCreateConfig: (config: FeedbackConfigSchema) => void;
  feedbackConfigs: FeedbackConfigSchema[];
  buttonTitle?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  defaultOpen?: boolean;
}) => {
  const [feedbackPopoverView, setFeedbackPopoverView] = useState<
    'configs' | 'new'
  >('configs');
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  //workaround to open the popover after the crud pane opens
  useEffect(() => {
    if (props.defaultOpen) {
      setTimeout(() => {
        setFeedbackPopoverOpen(true);
      }, 300);
    }
  }, [props.defaultOpen]);

  const feedbackConfigs = useMemo(() => {
    const allFeedbacks =
      searchTerm !== ''
        ? props.feedbackConfigs.filter((config) =>
            lowerCaseIncludes(config.feedback_key, searchTerm)
          )
        : props.feedbackConfigs;
    return allFeedbacks.slice(0, 50);
  }, [props.feedbackConfigs, searchTerm]);

  return (
    <PopoverDropdownMenu
      align="start"
      contentWidth="400px"
      isOpen={feedbackPopoverOpen}
      setIsOpen={setFeedbackPopoverOpen}
      values={feedbackConfigs}
      controlledSearchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      getTitle={(value) => value.feedback_key}
      getSubtitle={(value) => getFeedbackConfigSubtitle(value)}
      title={'Add feedback'}
      side="left"
      headerRightDecorator={
        <Button
          variant="outlined"
          color="neutral"
          size="sm"
          onClick={() => setFeedbackPopoverView('new')}
        >
          <div className="flex items-center gap-3">
            <PlusIcon className="h-3 w-3" /> <span>Create new</span>
          </div>
        </Button>
      }
      onSelect={props.onSelect}
      getRowTitleComponent={(value) => (
        <div className="mb-1">
          <FeedbackChip feedback_key={value.feedback_key} />
        </div>
      )}
      maxDropdownHeight={430}
      overrideContent={
        feedbackPopoverView === 'new' ? (
          <div className="flex flex-col gap-5 p-4">
            <div className="flex items-center gap-2">
              <IconButton
                size="sm"
                variant="outlined"
                onClick={() => setFeedbackPopoverView('configs')}
              >
                <ChevronLeftIcon className="h-3 w-3" />
              </IconButton>
              <div>Creating new feedback config</div>
            </div>
            <FeedbackConfigForm
              onSuccess={(config) => {
                props.onSelect(config);
                props.onCreateConfig(config);
                setFeedbackPopoverView('configs');
                setFeedbackPopoverOpen(false);
              }}
            />
          </div>
        ) : undefined
      }
      enableSearch
      getIsSelected={() => false}
      asChild
    >
      <Button variant="outlined" color="neutral" size={props.buttonSize}>
        <div className="flex items-center gap-2">
          <PlusIcon className="size-4" />{' '}
          <span>{props.buttonTitle ?? 'Desired feedback'}</span>
        </div>
      </Button>
    </PopoverDropdownMenu>
  );
};

const truncateString = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const AnnotationQueueCrudPane = ({
  closeModal,
  onSuccess,
  existingAnnotationQueue,
  open,
  focusOn,
  sessionIds,
  numRunsToAnnotate,
  sessionNamesToAnnotate,
}: AnnotationQueueCrudPaneProps) => {
  const runsToAddInfo = useMemo(() => {
    if (sessionNamesToAnnotate && numRunsToAnnotate) {
      const numSessions = sessionNamesToAnnotate.length;
      if (numSessions === 0) return undefined;
      const numRuns = numRunsToAnnotate;
      const sessionNames = sessionNamesToAnnotate
        .slice(0, 2)
        .map((name) => truncateString(name, 35));
      const numRemaining = numSessions - sessionNames.length;
      const sessionNamesString = sessionNames.join(', ');
      const remainingString =
        numRemaining > 0 ? ` and ${numRemaining} more experiments` : '';
      return `Create new annotate with ${numRuns} ${
        numRuns === 1 ? 'run' : 'runs'
      } from ${sessionNamesString}${remainingString}`;
    }
    return undefined;
  }, [numRunsToAnnotate, sessionNamesToAnnotate]);

  return (
    <Pane
      open={open}
      onClose={closeModal}
      title={
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start gap-3">
            <div className="flex w-full items-center justify-between">
              <div className="inline-flex items-center justify-center rounded-full bg-brand-tertiary p-1">
                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-white">
                  <EditIcon className="h-4 w-4 fill-white" />
                </div>
              </div>
            </div>
          </div>
          <div>
            {runsToAddInfo
              ? runsToAddInfo
              : existingAnnotationQueue
              ? `Edit Queue '${existingAnnotationQueue.name}'`
              : 'Create Annotation Queue'}
          </div>
        </div>
      }
      className="relative"
    >
      <AnnotationQueueCrud
        focusOn={focusOn}
        key={existingAnnotationQueue?.id}
        existingAnnotationQueue={existingAnnotationQueue}
        closeModal={closeModal}
        onSuccess={onSuccess}
        sessionIds={sessionIds}
        numRunsToAnnotate={numRunsToAnnotate}
        sessionNamesToAnnotate={sessionNamesToAnnotate}
      />
    </Pane>
  );
};

const AnnotationQueueCrud = ({
  existingAnnotationQueue,
  closeModal,
  onSuccess,
  focusOn,
  sessionIds,
}: AnnotationQueueCrudProps) => {
  'use no memo';
  const { data: fetchedQueue, isLoading } = useAnnotationQueue(
    existingAnnotationQueue?.id
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
    control,
    watch,
    trigger,
    setError,
    reset,
    setValue,
  } = useForm<AnnotationQueueFormInput>({
    mode: 'onChange',
    defaultValues: {
      numReviewersPerItem: fetchedQueue
        ? fetchedQueue?.num_reviewers_per_item?.toString()
        : '1',
      allWorkspaceMembersReview: fetchedQueue
        ? !fetchedQueue.num_reviewers_per_item
        : false,
      enableReservations: fetchedQueue
        ? !!fetchedQueue.enable_reservations
        : true,
      reservationHours: fetchedQueue?.reservation_minutes
        ? Math.floor(fetchedQueue.reservation_minutes / 60)
        : 0,
      reservationMinutes: fetchedQueue?.reservation_minutes
        ? fetchedQueue.reservation_minutes % 60
        : 1,
      feedbackRubricItems: fetchedQueue?.rubric_items ?? [],
      rubricInstructions: fetchedQueue?.rubric_instructions,
    },
  });

  const { trigger: createTrigger, isMutating: isMutatingCreate } =
    useCreateAnnotationQueueMutation();
  const { trigger: updateTrigger, isMutating: isMutatingUpdate } =
    useUpdateAnnotationQueueMutation({
      queue_id: fetchedQueue?.id as string,
    });
  const { createToast } = useToast();
  const annotationQueueCrudRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusOn && ['instructions', 'feedback'].includes(focusOn)) {
      annotationQueueCrudRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [focusOn]);

  const allWorkspaceMembersReview = watch('allWorkspaceMembersReview');
  const enableReservations = watch('enableReservations');
  const feedbackRubricInstructions = watch('rubricInstructions');

  const {
    trigger: createTagsOnResource,
    tagsOnResource,
    setTagsOnResource,
  } = useCreateTagsOnResource(ResourceType.Queue);

  const onSubmit: SubmitHandler<AnnotationQueueFormInput> = async (data) => {
    if (!data.allWorkspaceMembersReview && !data.numReviewersPerItem) {
      setError('numReviewersPerItem', {
        type: 'required',
      });
      return;
    }
    if (fetchedQueue) {
      await updateTrigger(
        {
          json: {
            name: data.name,
            description: data.description,
            default_dataset: data.datasetId ?? undefined,
            num_reviewers_per_item:
              data.allWorkspaceMembersReview || !data.numReviewersPerItem
                ? null
                : parseInt(data.numReviewersPerItem),
            enable_reservations: data.enableReservations,
            reservation_minutes:
              data.reservationHours * 60 + data.reservationMinutes,
            rubric_items: data.feedbackRubricItems,
            rubric_instructions: data.rubricInstructions,
          },
        },
        {
          onSuccess: (data) => {
            resetForm();
            closeModal();
            onSuccess(data);
            mutate((key: FetcherParams) =>
              key?.url?.startsWith(apiAnnotationQueuesPath)
            );
          },
          onError: (e) => {
            createToast({
              title: 'Error updating annotation queue',
              description: e.message,
              error: true,
            });
          },
        }
      );
    } else {
      await createTrigger(
        {
          json: {
            name: data.name,
            description: data.description,
            default_dataset: data.datasetId ?? undefined,
            num_reviewers_per_item:
              data.allWorkspaceMembersReview || !data.numReviewersPerItem
                ? null
                : parseInt(data.numReviewersPerItem),
            enable_reservations: data.enableReservations,
            reservation_minutes:
              data.reservationHours * 60 + data.reservationMinutes,
            rubric_items: data.feedbackRubricItems,
            rubric_instructions: data.rubricInstructions,
            session_ids: sessionIds,
          },
        },
        {
          onSuccess: async (data) => {
            await createTagsOnResource(data.id);
            resetForm();
            closeModal();
            onSuccess(data);
            mutate((key: FetcherParams) =>
              key?.url?.startsWith(apiAnnotationQueuesPath)
            );
          },
          onError: (e) => {
            createToast({
              title: 'Error creating annotation queue',
              description: e.message,
              error: true,
            });
          },
        }
      );
    }
  };

  useEffect(() => {
    if (fetchedQueue) {
      reset({
        numReviewersPerItem: fetchedQueue
          ? fetchedQueue?.num_reviewers_per_item?.toString()
          : '1',
        allWorkspaceMembersReview: fetchedQueue
          ? !fetchedQueue.num_reviewers_per_item
          : false,
        enableReservations: fetchedQueue
          ? !!fetchedQueue.enable_reservations
          : true,
        reservationHours: fetchedQueue?.reservation_minutes
          ? Math.floor(fetchedQueue.reservation_minutes / 60)
          : 0,
        reservationMinutes: fetchedQueue?.reservation_minutes
          ? fetchedQueue.reservation_minutes % 60
          : 1,
        feedbackRubricItems: fetchedQueue?.rubric_items ?? [],
        rubricInstructions: fetchedQueue?.rubric_instructions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedQueue?.num_reviewers_per_item,
    fetchedQueue?.enable_reservations,
    fetchedQueue?.reservation_minutes,
  ]);

  const watchedFeedbackRubricItems = watch('feedbackRubricItems');

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <CircularProgress size="lg" />
      </div>
    );
  }

  const mainFormComponent = (
    <div className="flex w-full flex-col gap-5">
      <div className="flex w-full flex-col gap-5 px-10 py-4 pt-8">
        <div className="text-xl font-medium">Basic Details</div>
        <div className="flex w-full flex-col gap-5">
          <FormControl error={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              defaultValue={fetchedQueue?.name}
              placeholder="Enter name"
              className="rounded-md border border-secondary px-2 py-1.5"
              {...register('name', { required: true })}
            />
            {errors.name?.type === 'required' && (
              <FormHelperText className="text-xs text-error">
                Name is required
              </FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input
              defaultValue={fetchedQueue?.description}
              placeholder="Enter description"
              className="rounded-md border border-secondary px-2 py-1.5"
              {...register('description')}
            />
          </FormControl>
          {!fetchedQueue && (
            <FormControl>
              <ResourceTagCrudPaneEditor
                tagsOnResource={tagsOnResource}
                setTagsOnResource={setTagsOnResource}
              />
            </FormControl>
          )}
          {!fetchedQueue?.run_rule_id && (
            <div className="flex flex-col">
              <Controller
                control={control}
                name="datasetId"
                defaultValue={fetchedQueue?.default_dataset ?? undefined}
                render={({ field }) => (
                  <DatasetPicker
                    datasetId={field.value ?? undefined}
                    onChange={field.onChange}
                    label={'Select a default dataset'}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
      <Divider />
      <div
        className="flex w-full flex-col gap-5 px-10 py-4"
        ref={annotationQueueCrudRef}
      >
        <AnnotationQueueRubricCrud
          control={control}
          feedbackRubricInstructions={feedbackRubricInstructions}
          onChangeInstructions={(val) => setValue('rubricInstructions', val)}
          errors={errors}
          feedbackRubricItems={watchedFeedbackRubricItems}
          focusOn={focusOn}
          isEvaluatorMode={!!fetchedQueue?.run_rule_id}
        />
      </div>

      <Divider />

      <div className="flex w-full flex-col gap-5 px-10 py-4 pb-10">
        <div className="text-xl font-medium">Collaborator Settings</div>
        <div className="flex flex-col gap-2">
          <FormControl error={!!errors.numReviewersPerItem}>
            <div className="flex flex-col gap-1">
              <FormLabel
                sx={{
                  my: 'auto',
                  color: allWorkspaceMembersReview ? 'lightgray' : '',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >
                Number of reviewers per run
                <Tooltip
                  title="Reviewers per Run"
                  description="This sets the number of reviewers required to mark a run as 'Done' before it is removed from the queue."
                  side="top"
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <InfoCircleIcon
                      height={20}
                      className="my-auto cursor-pointer"
                    />
                  </div>
                </Tooltip>
              </FormLabel>
              <Input
                defaultValue={fetchedQueue?.num_reviewers_per_item}
                placeholder="ex. 2"
                className="rounded-md border border-secondary px-2 py-1.5"
                disabled={!!allWorkspaceMembersReview}
                {...register('numReviewersPerItem', {
                  validate: (value) => {
                    if (!value) return true;
                    const numValue = Number(value);
                    return (
                      (Number.isInteger(numValue) && numValue > 0) ||
                      'Please enter a whole number greater than 1.'
                    );
                  },
                })}
              />
            </div>
            {errors.numReviewersPerItem && (
              <FormHelperText className="text-xs text-error">
                {errors.numReviewersPerItem.message}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <div className="mb-auto flex flex-row gap-2">
              <Controller
                name="allWorkspaceMembersReview"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('enableReservations', false);
                      }
                    }}
                    className="my-auto"
                  />
                )}
              />
              <FormLabel sx={{ my: 'auto' }}>
                All workspace members review each run
              </FormLabel>
            </div>
          </FormControl>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2">
            <FormControl>
              <Controller
                name="enableReservations"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center">
                    <FormLabel sx={{ mr: 1 }}>
                      Enable reservations on runs
                    </FormLabel>
                    <Switch
                      checked={field.value}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue('allWorkspaceMembersReview', false);
                        }
                        field.onChange(e.target.checked);
                      }}
                    />
                  </div>
                )}
              />
            </FormControl>
            <Tooltip
              title="Run Reservations"
              description="Reserving a run locks it for your review for a set amount of time. Multiple reservations on the same run are possible if multiple reviewers are enabled. Reservations cannot be enabled if all workspace members review each run."
              side="top"
            >
              <div onClick={(e) => e.stopPropagation()}>
                <InfoCircleIcon
                  height={20}
                  className="my-auto cursor-pointer"
                />
              </div>
            </Tooltip>
          </div>
          <FormControl>
            <FormLabel sx={{ color: !enableReservations ? 'lightgray' : '' }}>
              Reservation length
            </FormLabel>
            <div className="flex items-center gap-2">
              <FormControl error={!!errors.reservationHours}>
                <Controller
                  name="reservationHours"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value === undefined) return true;
                      return (
                        (Number.isInteger(value) && value >= 0) ||
                        'Please enter a non-negative whole number for hours.'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="Hours"
                      className="w-50 rounded-md border border-secondary px-2 py-1.5"
                      disabled={!enableReservations}
                      onChange={(e) => {
                        field.onChange(
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value)
                        );
                        trigger('reservationMinutes');
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormLabel
                sx={{
                  my: 'auto',
                  color: !enableReservations ? 'lightgray' : '',
                }}
              >
                hours
              </FormLabel>
              <FormControl error={!!errors.reservationMinutes}>
                <Controller
                  name="reservationMinutes"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const hours = Number(
                        control._formValues.reservationHours
                      );
                      if (value === undefined) return true;
                      return (
                        (Number.isInteger(value) &&
                          (hours === 0 ? value > 0 : value >= 0) &&
                          value < 60) ||
                        `Please enter a whole number between ${
                          hours === 0 ? 1 : 0
                        } and 59 for minutes.`
                      );
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="Minutes"
                      className="w-50 rounded-md border border-secondary px-2 py-1.5"
                      disabled={!enableReservations}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value)
                        )
                      }
                    />
                  )}
                />
              </FormControl>
              <FormLabel
                sx={{
                  my: 'auto',
                  color: !enableReservations ? 'lightgray' : '',
                }}
              >
                minutes
              </FormLabel>
            </div>
            {errors.reservationHours && (
              <FormHelperText className="text-xs text-error">
                {errors.reservationHours.message}
              </FormHelperText>
            )}
            {errors.reservationMinutes && (
              <FormHelperText className="text-xs text-error">
                {errors.reservationMinutes.message}
              </FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
    </div>
  );

  return (
    <form
      className={cn(
        'absolute bottom-0 left-0 right-0 top-0 mx-auto flex w-full flex-col items-stretch gap-2'
      )}
      onSubmit={handleSubmit(onSubmit)}
    >
      {mainFormComponent}
      <TopBarPaneSlot.Fill>
        <div className="mr-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="outlined"
            color="neutral"
            onClick={() => closeModal()}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color={'primary'}
            loading={isMutatingCreate || isMutatingUpdate}
            disabled={!!errors.name}
            startDecorator={
              fetchedQueue ? (
                <SimpleEditIcon className="stroke-current" />
              ) : (
                <PlusIcon className="h-5 w-5" />
              )
            }
            sx={{ px: 2 }}
            onClick={handleSubmit(onSubmit)}
          >
            {fetchedQueue ? 'Save' : 'Create'}
          </Button>
        </div>
      </TopBarPaneSlot.Fill>
    </form>
  );
};
